/**
 * PannableImage — inline drag-to-reposition image within a scale-to-fill frame.
 *
 * Uses CSS object-cover + object-position instead of absolute positioning, so
 * the image is always immediately visible at the correct center crop (identical
 * to the compact thumbnails). Drag updates object-position in real time.
 * Natural size and container size are measured in the background solely for
 * emitting resolution-independent CropParams on pointer release.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Move } from 'lucide-react';
import type { CropParams } from '@/components/media/mediaAdjustmentTypes';

export interface PannableImageProps {
  src: string;
  /** Target output dimensions — define aspect ratio and publish pixel size. */
  outputWidth: number;
  outputHeight: number;
  existingCrop?: CropParams;
  onCropChange?: (crop: CropParams) => void;
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function computeScaleToFill(nW: number, nH: number, targetAR: number) {
  const imageAR = nW / nH;
  const w_frac = imageAR > targetAR ? targetAR / imageAR : 1.0;
  const h_frac = imageAR > targetAR ? 1.0 : imageAR / targetAR;
  return { w_frac, h_frac, maxPanX: Math.max(0, 1 - w_frac), maxPanY: Math.max(0, 1 - h_frac) };
}

/** Overflow in CSS pixels for a given image + container pair. */
function computeOverflow(nW: number, nH: number, cW: number, cH: number) {
  const imageAR = nW / nH;
  const containerAR = cW / cH;
  if (imageAR > containerAR) {
    const renderedW = cH * imageAR;
    return { x: Math.max(0, renderedW - cW), y: 0 };
  }
  const renderedH = cW / imageAR;
  return { x: 0, y: Math.max(0, renderedH - cH) };
}

export const PannableImage: React.FC<PannableImageProps> = ({
  src,
  outputWidth,
  outputHeight,
  existingCrop,
  onCropChange,
}) => {
  const targetAR = outputWidth / outputHeight;
  const containerRef = useRef<HTMLDivElement>(null);

  const [panFrac, setPanFrac] = useState<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);
  const [hovering, setHovering] = useState(false);

  const isDragging = useRef(false);
  const didMove = useRef(false);
  const dragStart = useRef<{ mx: number; my: number; pf: { x: number; y: number } } | null>(null);

  // Measure container
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainerSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Restore pan from existing crop once natural size is known
  useEffect(() => {
    if (!naturalSize || !existingCrop) return;
    const fill = computeScaleToFill(naturalSize.w, naturalSize.h, targetAR);
    setPanFrac({
      x: fill.maxPanX > 0 ? clamp(existingCrop.x_frac / fill.maxPanX, 0, 1) : 0.5,
      y: fill.maxPanY > 0 ? clamp(existingCrop.y_frac / fill.maxPanY, 0, 1) : 0.5,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naturalSize]);

  const overflow = useMemo(() => {
    if (!naturalSize || !containerSize) return { x: 0, y: 0 };
    return computeOverflow(naturalSize.w, naturalSize.h, containerSize.w, containerSize.h);
  }, [naturalSize, containerSize]);

  const canPan = overflow.x > 1 || overflow.y > 1;

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!canPan) return;
      e.preventDefault();
      isDragging.current = true;
      didMove.current = false;
      dragStart.current = { mx: e.clientX, my: e.clientY, pf: { ...panFrac } };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [canPan, panFrac],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging.current || !dragStart.current) return;
      e.preventDefault();
      const dx = e.clientX - dragStart.current.mx;
      const dy = e.clientY - dragStart.current.my;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didMove.current = true;
      setPanFrac({
        x: overflow.x > 0 ? clamp(dragStart.current.pf.x - dx / overflow.x, 0, 1) : 0.5,
        y: overflow.y > 0 ? clamp(dragStart.current.pf.y - dy / overflow.y, 0, 1) : 0.5,
      });
    },
    [overflow],
  );

  const onPointerUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (!didMove.current || !naturalSize) {
      dragStart.current = null;
      return;
    }
    dragStart.current = null;
    const fill = computeScaleToFill(naturalSize.w, naturalSize.h, targetAR);
    onCropChange?.({
      x_frac: panFrac.x * fill.maxPanX,
      y_frac: panFrac.y * fill.maxPanY,
      w_frac: fill.w_frac,
      h_frac: fill.h_frac,
    });
  }, [naturalSize, targetAR, panFrac, onCropChange]);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden"
      style={{ cursor: canPan ? 'grab' : 'default', touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      {/* Single image — object-position drives the pan, matching thumbnail behavior */}
      <img
        src={src}
        className="h-full w-full object-cover"
        style={{ objectPosition: `${panFrac.x * 100}% ${panFrac.y * 100}%`, userSelect: 'none' }}
        draggable={false}
        alt=""
        onLoad={(e) => {
          const img = e.currentTarget;
          setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
        }}
      />

      {/* Drag hint */}
      {canPan && hovering && !isDragging.current && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex items-center gap-1.5 rounded-full bg-black/50 px-3 py-1.5 text-[11px] text-white backdrop-blur-sm">
            <Move size={12} />
            Drag to reposition
          </div>
        </div>
      )}
    </div>
  );
};
